import { sb } from '../core/db';
import { isNativeApp } from '../core/platform';
import {
  mapClient, mapNotif, mapPayment, mapSub, mapTask, mapInvoice, resolveInvoiceFinancials,
  isClientPortalRole,
} from '../core/helpers';

export function isMobileClientUser(user) {
  return isNativeApp() && isClientPortalRole(user?.role);
}

/** Native: always paginate via API (server scopes to client's companies). Web: single-company only. */
export function clientPortalUsesApiPaging(singleCompany) {
  return isNativeApp() || !!singleCompany;
}

function scopedCompanyIdsFrom(currentUser, linksRaw, companiesRaw) {
  const ids = new Set(
    (Array.isArray(linksRaw) ? linksRaw : [])
      .filter((l) => l.user_id === currentUser.id)
      .map((l) => l.company_id),
  );
  if ((companiesRaw || []).some((c) => c.id === currentUser.id)) ids.add(currentUser.id);
  return ids;
}

/** 3 parallel reads → Live. No tasks/invoices bulk fetch. */
export async function loadMobileClientBootstrap(currentUser, apply) {
  const [linksRaw, companiesRaw, notifsRaw] = await Promise.all([
    sb.select('user_companies', `user_id=eq.${currentUser.id}`),
    sb.select('companies'),
    sb.select('notifications', `user_id=eq.${currentUser.id}`).catch(() => []),
  ]);

  apply.setUserLinks(Array.isArray(linksRaw) ? linksRaw : []);

  const scopedCompanyIds = scopedCompanyIdsFrom(currentUser, linksRaw, companiesRaw);
  const clientList = (companiesRaw || [])
    .filter((c) => scopedCompanyIds.has(c.id))
    .map(mapClient);

  apply.setClients(clientList);
  apply.setAllUsers([{
    id: currentUser.id,
    name: currentUser.name,
    role: currentUser.role,
    email: currentUser.email,
    phone: currentUser.phone,
    avatar: currentUser.avatar,
    color: currentUser.color,
    permissions: currentUser.permissions || {},
  }]);
  apply.setEmps([]);
  apply.setNotifs(
    (Array.isArray(notifsRaw) ? notifsRaw : [])
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .map(mapNotif),
  );
  apply.setDbReady(true);

  return { scopedCompanyIds };
}

function mapScheduleRow(r) {
  return {
    id: r.id,
    invoiceId: r.invoice_id,
    companyId: r.company_id,
    seq: r.seq || 1,
    label: r.label,
    amount: Number(r.amount) || 0,
    dueDate: r.due_date,
    triggerEvent: r.trigger_event,
    status: r.status || 'unpaid',
    paidAmount: Number(r.paid_amount) || 0,
  };
}

function mapInvoiceRow(raw, paymentsList) {
  const inv = mapInvoice(raw);
  const lines = raw.line_items || [];
  inv.lineItems = lines
    .sort((a, b) => (a.seq || 0) - (b.seq || 0))
    .map((l) => ({
      id: l.id, name: l.name, type: l.type, sac: l.sac,
      qty: l.qty || 1, unitPrice: Number(l.unit_price) || 0,
      gst: l.gst, amount: Number(l.amount) || 0,
    }));
  return resolveInvoiceFinancials(inv, paymentsList);
}

/** Background: first pages only — Pay Now + home stats + task actions. */
export function hydrateMobileClientPortal(scopedCompanyIds, apply) {
  const ids = [...scopedCompanyIds];
  const companyFilter = ids.length === 1 ? `company_id=eq.${ids[0]}` : '';
  const inScope = (row) => scopedCompanyIds.has(row.company_id || row.client_id || row.id);

  (async () => {
    try {
      const [scheduleRaw, paymentsPage, subsPage, tasksPage, invPage] = await Promise.all([
        sb.select('payment_schedule'),
        sb.selectPage('payments', companyFilter, { limit: 50 }),
        sb.selectPage('submissions', companyFilter, { limit: 50 }),
        sb.selectPage('tasks', companyFilter, { limit: 50 }),
        sb.selectPage('invoices', companyFilter, { limit: 50 }),
      ]);

      apply.setPaymentSchedule(
        (Array.isArray(scheduleRaw) ? scheduleRaw : []).filter(inScope).map(mapScheduleRow),
      );

      const paymentsList = (paymentsPage.items || []).map(mapPayment);
      apply.setPayments(paymentsList);
      apply.setSubs((subsPage.items || []).map(mapSub));
      apply.setTasks((tasksPage.items || []).map(mapTask));
      apply.setInvoices((invPage.items || []).map((raw) => mapInvoiceRow(raw, paymentsList)));
    } catch (e) {
      console.warn('mobile client hydrate:', e.message);
    }
  })();
}
